<script setup lang="ts">
import { computed, watch, nextTick, reactive, ref, toRef } from 'vue';
import { useEventListener, useWindowScroll } from '@vueuse/core';

import { AppFadeInOutTransition } from '../transitions';

import type {
  AppTooltipConfig,
  AppTooltipPosition,
  AppTriggerConfig,
} from './AppTooltip.model';
import { adjustTooltipPosition } from './AppTooltip.helper';

export interface AppTooltipProps {
  position: AppTooltipPosition;
}
const props = defineProps<AppTooltipProps>();

const scrollYPosition = useWindowScroll().y;

// =====================================================
// ========== NOTE: Handle tooltip visibility ==========
// =====================================================

const isVisibleByHover = ref<boolean>(false);
const isVisibleByClick = ref<boolean>(false);
const isVisible = computed<boolean>(
  () => isVisibleByHover.value || isVisibleByClick.value
);

function show(): void {
  isVisibleByHover.value = true;
  renderTooltip();
}

function hide(): void {
  isVisibleByHover.value = false;
  if (!isVisibleByClick.value) {
    resetDefaultPosition();
  }
}

function hideByOverlayClicked(): void {
  isVisibleByClick.value = false;
  resetDefaultPosition();
}

function toggle(): void {
  isVisibleByClick.value = !isVisibleByClick.value;
  if (!isVisibleByClick.value) {
    resetDefaultPosition();
  }
}

// ===================================================
// ========== NOTE: Handle tooltip position ==========
// ===================================================

const defaultPosition = toRef<AppTooltipPosition>(props.position);

const triggerRef = ref<HTMLElement | null>(null);
const triggerConfig = reactive<AppTriggerConfig>({
  XPosition: 0,
  YPosition: 0,
  width: 0,
  height: 0,
});

const tooltipRef = ref<HTMLElement | null>(null);
const tooltipConfig = reactive<AppTooltipConfig>({
  topPosition: '',
  leftPosition: '',
  height: 0,
});

function resetDefaultPosition(): void {
  defaultPosition.value = props.position;
}

function renderTooltip(): void {
  calculateTriggerProperties();

  nextTick(() => {
    if (!tooltipRef.value) {
      return;
    }
    defaultPosition.value = adjustTooltipPosition(
      tooltipRef.value,
      defaultPosition.value,
      triggerConfig,
      tooltipConfig
    );
  });
}

function calculateTriggerProperties(): void {
  const trigger = triggerRef.value?.getBoundingClientRect();
  triggerConfig.width = trigger?.width ?? 0;
  triggerConfig.height = trigger?.height ?? 0;
  triggerConfig.XPosition = trigger?.x ?? 0;
  triggerConfig.YPosition = (trigger?.y ?? 0) + scrollYPosition.value;
}

// ================================================
// ========== NOTE: Handle window resize ==========
// ================================================

watch(
  () => props.position,
  () => {
    defaultPosition.value = props.position;
    renderTooltip();
  }
);

useEventListener(window, 'resize', handleResize);

function handleResize(): void {
  if (!isVisible.value) {
    return;
  }
  renderTooltip();
}
</script>

<template>
  <button
    class="app-tooltip-container"
    @mouseenter="show"
    @mouseleave="hide"
    @focusin="show"
    @focusout="hide"
    @click.stop="toggle"
  >
    <div ref="triggerRef">
      <slot name="trigger" />
    </div>
    <Teleport v-if="isVisible" to="body">
      <AppFadeInOutTransition>
        <div
          ref="tooltipRef"
          class="tooltip"
          :class="defaultPosition"
          :style="{
            width: `${tooltipConfig.width}px`,
            top: tooltipConfig.topPosition,
            left: tooltipConfig.leftPosition,
          }"
        >
          <slot />
        </div>
      </AppFadeInOutTransition>
      <button
        v-if="isVisibleByClick"
        class="overlay"
        @click.stop="hideByOverlayClicked"
      />
    </Teleport>
  </button>
</template>

<style scoped lang="scss">
@import '../../styles/_fonts.scss';

.app-tooltip-container {
  position: relative;
  display: inline-block;
}

.tooltip {
  box-sizing: border-box;
  @include font-label-l-light;
  position: absolute;
  background-color: var(--color-neutral-70);
  color: var(--color-white);
  padding: var(--spacing-12);
  border-radius: var(--border-radius-4);
  box-shadow: var(--shadow);
  line-height: var(--line-heights-24);
  z-index: var(--modal-z-index);

  &::before {
    content: '';
    position: absolute;
    border: var(--spacing-4) solid transparent;
  }

  &.top::before {
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border-top-color: var(--color-neutral-70);
  }
  &.bottom::before {
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    border-bottom-color: var(--color-neutral-70);
  }

  &.left::before {
    left: 100%;
    top: 50%;
    transform: translateY(-50%);
    border-left-color: var(--color-neutral-70);
  }
  &.right::before {
    right: 100%;
    top: 50%;
    transform: translateY(-50%);
    border-right-color: var(--color-neutral-70);
  }
  &.top-right::before {
    top: 100%;
    left: var(--spacing-4);
    border-top-color: var(--color-neutral-70);
  }
  &.top-left::before {
    top: 100%;
    right: var(--spacing-4);
    border-top-color: var(--color-neutral-70);
  }

  &.bottom-right::before {
    bottom: 100%;
    left: var(--spacing-4);
    border-bottom-color: var(--color-neutral-70);
  }
  &.bottom-left::before {
    bottom: 100%;
    right: var(--spacing-4);
    border-bottom-color: var(--color-neutral-70);
  }
}

.overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: var(--modal-z-index);
  background: transparent;
}
</style>
